import { useEffect, useState } from 'react';
import { useInteraction } from 'src/common/hooks/useInteraction';
import { GQLInteractionType } from 'src/generated/graph-api';
import { useCoverSheetQuery } from 'src/views/Coversheet/hooks/useCoverSheetQuery';
import { useSetMissingFields } from 'src/views/Coversheet/state/coverSheetValidation';
import { computeMissingFields } from 'src/views/Coversheet/utils/computeMissingFields';
import { useCoverSheetConfig } from '../useCoverSheetConfig';

// Cards the Cover Sheet renders as field-less substitutes for unrepresented
// buyers (see SectionRenderer's UnrepresentedBuyerAgentCard swap). They must
// be excluded from the off-route missing-fields computation so the Documents
// sidebar doesn't flag required fields the user can never fill.
const UNREPRESENTED_BUYER_EXCLUDED_CARD_IDS = ['buySideContacts.buyersAgent'];

export function useInitializeCoverSheetSideBar(
  interactionId: string,
  interactionType: GQLInteractionType,
  skip = false,
  hasUnrepresentedClient = false,
) {
  const skipRepresentationQuery =
    skip || interactionType !== GQLInteractionType.lease;
  const { data: leaseData, isLoading: leaseRepresentationLoading } =
    useInteraction(interactionId, 'lease', skipRepresentationQuery);
  const leaseRepresentation = leaseData?.representation ?? null;
  const { data: configData } = useCoverSheetConfig({
    interactionId,
    interactionType,
    representationType: leaseRepresentation,
    skip: skip || leaseRepresentationLoading,
  });
  const coverSheetConfig = configData?.coverSheetConfig;
  const { data: coverSheetData } = useCoverSheetQuery(
    interactionId,
    interactionType,
    configData,
  );

  const [initialized, setInitialized] = useState(false);
  const setMissingFields = useSetMissingFields(interactionId);

  useEffect(() => {
    // If skip is ever true, it's because we were on the Cover Sheet page, which
    // handles initialization itself. Once done, this never needs to re-initialize
    // even when navigating away from the Cover Sheet page.
    if (skip) setInitialized(true);
  }, [skip]);

  useEffect(() => {
    if (!coverSheetConfig || !coverSheetData || skip || initialized) return;
    setInitialized(true);
  }, [coverSheetConfig, coverSheetData, skip, initialized]);

  // Keep the missing-fields atom in sync from data so the Documents tab is
  // blocked / sidebar badges show even when the user lands directly on a
  // non-Cover-Sheet page (e.g. /cover-letter) without ever mounting the
  // per-field validators.
  useEffect(() => {
    if (skip || !coverSheetConfig || !coverSheetData) return;
    setMissingFields(
      computeMissingFields(
        coverSheetConfig,
        coverSheetData,
        hasUnrepresentedClient ? UNREPRESENTED_BUYER_EXCLUDED_CARD_IDS : [],
      ),
    );
  }, [
    skip,
    coverSheetConfig,
    coverSheetData,
    hasUnrepresentedClient,
    setMissingFields,
  ]);
}
